import { useState, useEffect } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { RefreshCw, Undo2 } from "lucide-react";

const ACTION_META = {
  description: { label: "Description edited", color: "bg-blue-600" },
  notes: { label: "Notes edited", color: "bg-sky-600" },
  families: { label: "Families changed", color: "bg-teal-600" },
  kind: { label: "Type changed", color: "bg-indigo-600" },
  archive: { label: "Archived", color: "bg-slate-600" },
  restore: { label: "Restored", color: "bg-green-600" },
  delete: { label: "Removed & blocked", color: "bg-red-600" },
  unblock: { label: "Unblocked", color: "bg-amber-600" },
  import: { label: "Price import", color: "bg-slate-900" },
  import_undo: { label: "Import undone", color: "bg-orange-600" },
  revert: { label: "Reverted", color: "bg-purple-600" },
  shipping_settings: { label: "Shipping notice edited", color: "bg-cyan-700" },
  season_reset: { label: "New season started", color: "bg-rose-700" },
};

export default function StaffChanges() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState({});

  const load = () => {
    setLoading(true);
    api.get("/admin/staff-activity").then((r) => setRows(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const showDetail = async (id) => {
    if (detail[id]) return;
    const { data } = await api.get(`/admin/staff-activity/${id}`);
    setDetail((d) => ({ ...d, [id]: data }));
  };

  const revert = async (e) => {
    if (!window.confirm(`Revert this change to ${e.sku}? The previous value will be restored.`)) return;
    try {
      await api.post(`/admin/staff-activity/${e.id}/revert`);
      toast.success("Change reverted");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  return (
    <div className="mt-10" data-testid="staff-changes-section">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h2 className="font-heading text-xl font-black tracking-tight text-slate-900">Staff changes</h2>
          <p className="text-sm text-slate-500">Admin-only: every catalog edit by staff, with one-click revert.</p>
        </div>
        <Button variant="outline" size="sm" className="rounded-sm" onClick={load} data-testid="refresh-staff-changes"><RefreshCw className="h-4 w-4 mr-2" /> Refresh</Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">When</th>
              <th className="text-left px-4 py-3">Staff</th>
              <th className="text-left px-4 py-3">Change</th>
              <th className="text-left px-4 py-3">SKU</th>
              <th className="text-left px-4 py-3">Detail</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody data-testid="staff-changes-table">
            {loading && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No staff changes recorded yet.</td></tr>}
            {rows.map((e) => {
              const meta = ACTION_META[e.action] || { label: e.action, color: "bg-slate-500" };
              const d = detail[e.id];
              return (
                <tr key={e.id} className={`border-t border-slate-100 even:bg-slate-50/50 hover:bg-blue-50/40 transition-colors ${e.reverted ? "opacity-60" : ""}`} data-testid={`staff-change-${e.id}`}>
                  <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{new Date(e.ts).toLocaleString()}</td>
                  <td className="px-4 py-2.5 font-medium text-slate-900">{e.user_name}</td>
                  <td className="px-4 py-2.5"><Badge className={`rounded-sm ${meta.color}`}>{meta.label}</Badge>{e.reverted && <span className="ml-2 text-xs text-slate-500">(reverted)</span>}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{e.sku || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-600 text-xs max-w-md">
                    {e.detail && <div>{e.detail}</div>}
                    {e.revertable && !d && <button className="text-blue-600 hover:underline" onClick={() => showDetail(e.id)} data-testid={`show-detail-${e.id}`}>Show before / after</button>}
                    {d && <div className="whitespace-pre-line break-words"><span className="text-red-600 line-through">{d.before_text}</span> → <span className="text-green-700">{d.after_text}</span></div>}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {e.revertable && !e.reverted && (
                      <Button size="sm" variant="outline" className="rounded-sm" onClick={() => revert(e)} data-testid={`revert-${e.id}`}><Undo2 className="h-3.5 w-3.5 mr-1" /> Revert</Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
